import { useEffect, useMemo, useState } from 'react';
import { createTeamInvite, getAthletes } from '../../api/client';
import { useLoad } from '../../hooks/useLoad';
import { useBusy } from '../../hooks/useBusy';
import { inlineError } from '../../hooks/useApiCall';
import { useToast } from '../../hooks/useToast';
import { useI18n } from '../../i18n';
import { personName, nameMatches } from '../../utils/personName';
import ModalBackdrop from './ModalBackdrop';
import ModalHeader from './ModalHeader';
import ModalFooter from './ModalFooter';
import type { AthleteEntry } from '../../types';
import Notice from './Notice';

/**
 * #658 — a coach invites an athlete to the team. The athlete approves the
 * invitation on the own profile; for a record with no account, an admin or a
 * team manager approves it. The list holds the active records of the other
 * teams and of no team. `initial` preselects a record: the create form opens
 * the modal with the record that has the same name and birth date.
 */
interface Props {
  teamId: string;
  teamName: string;
  initial?: Pick<AthleteEntry, 'id' | 'first_name' | 'last_name' | 'team_name'> | null;
  onClose: () => void;
}

export default function InviteAthleteModal({ teamId, teamName, initial, onClose }: Props) {
  const { t } = useI18n();
  const m = t.teams.invite;
  const { showSuccess } = useToast();
  const sending = useBusy();
  const [query, setQuery] = useState('');
  const [chosen, setChosen] = useState<Props['initial']>(initial ?? null);
  const [error, setError] = useState('');

  // The picker keeps the failure inline (`error`), as before. The hook drops a late answer.
  const { data: records } = useLoad(
    () => getAthletes().then(r => r.data.filter(a => a.is_active !== false && a.team_id !== teamId)),
    [teamId],
    { context: 'InviteAthleteModal:athletes', initial: [] as AthleteEntry[], expected: () => true, onError: e => setError(inlineError(e)) },
  );

  const matches = useMemo(
    () => (query.trim() ? records.filter(a => nameMatches(query, a.first_name, a.last_name)).slice(0, 8) : []),
    [records, query],
  );

  async function send() {
    if (!chosen) return;
    const name = personName(chosen.first_name, chosen.last_name);
    await sending.run(async () => {
      setError('');
      try {
        await createTeamInvite(teamId, chosen.id);
        showSuccess(m.sent.replace('{name}', name));
        onClose();
      } catch (err) {
        setError(inlineError(err));
      }
    });
  }

  return (
    <ModalBackdrop onClose={onClose} maxWidth="md" ariaLabel={m.title}>
      <ModalHeader title={m.title} subtitle={teamName} />
      <div className="space-y-3">
        <p className="text-sm text-gray-600">{m.hint}</p>
        <div>
          <input
            className="input" data-autofocus data-testid="invite-search"
            placeholder={m.search} value={query} onChange={e => setQuery(e.target.value)}
          />
          {query.trim() && (
            <ul className="mt-1 border border-gray-200 rounded-md divide-y divide-gray-100 max-h-64 overflow-y-auto">
              {matches.length === 0 && <li className="px-3 py-2 text-sm text-gray-500">{m.noMatch}</li>}
              {matches.map(a => (
                <li key={a.id}>
                  <button type="button" onClick={() => { setChosen(a); setQuery(''); setError(''); }}
                    data-testid={`invite-candidate-${a.id}`}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50">
                    <span className="font-medium">{personName(a.first_name, a.last_name)}</span>
                    <span className="text-gray-500"> · {a.team_name ?? m.noTeam}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        {chosen && (
          <div className="card p-3 text-sm" data-testid="invite-chosen">
            <div className="font-medium">{personName(chosen.first_name, chosen.last_name)}</div>
            <div className="text-gray-500">{chosen.team_name ?? m.noTeam}</div>
          </div>
        )}
        {error && <Notice tone="error" plain data-testid="invite-error">{error}</Notice>}
      </div>
      <ModalFooter onSave={send} busy={sending.busy} saveLabel={m.send}
        saveDisabled={!chosen} onCancel={onClose} />
    </ModalBackdrop>
  );
}
